const Privacy = () => (
  <div className="max-w-3xl mx-auto space-y-6">

    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Privacy Policy</h1>
    {/* Update this date manually whenever the policy content changes. */}
    <p className="text-sm text-muted-foreground">Last updated: 9 June 2026</p>

    <div className="prose prose-sm dark:prose-invert max-w-none space-y-4">
      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">Our Commitment</h2>
        <p className="text-muted-foreground">
          EmbedKit is built on a simple principle: <strong>your widget inputs stay with you</strong>. We never collect, store, or transmit the values you enter into our calculators, converters, or other widgets. The only data we receive is aggregate website traffic telemetry, described below.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">What We Don't Collect</h2>
        <ul className="list-disc pl-5 text-muted-foreground space-y-1">
          <li>No widget inputs, calculator values, or user-entered data</li>
          <li>No user accounts or sign-in identifiers</li>
          <li>No cross-site advertising identifiers</li>
          <li>No third-party advertising or marketing pixels</li>
          <li>No fingerprinting beyond what Google Analytics collects by default (see below)</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">Analytics (Google Tag Manager &amp; Google Analytics)</h2>
        <p className="text-muted-foreground">
          The <code>embedkit.tech</code> marketing site loads Google Tag Manager (container <code>GTM-NNV3Z2KW</code>), which in turn loads Google Analytics. This is used solely for aggregate traffic and feature-usage telemetry (page views, referrers, approximate location, device type). It is <strong>not loaded inside embedded widgets</strong> running on third-party websites.
        </p>
        <p className="text-muted-foreground">As part of Google Analytics, the following cookies and data may be set on the marketing site:</p>
        <ul className="list-disc pl-5 text-muted-foreground space-y-1">
          <li><code>_ga</code>, <code>_ga_*</code>, <code>_gid</code>, <code>_gat</code>, Google Analytics cookies used to distinguish visitors and throttle requests</li>
          <li>IP address (truncated by Google), user-agent string, and basic device/browser metadata</li>
        </ul>
        <p className="text-muted-foreground">
          See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google's Privacy Policy</a> for how Google processes this data. You can opt out by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Analytics opt-out browser add-on</a> or by blocking these scripts with your browser or an extension.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">How Our Widgets Work</h2>
        <p className="text-muted-foreground">
          All EmbedKit widget computations (calculations, conversions, formatting) run entirely in the user's browser. We do not operate a backend that receives widget inputs, and embedded widgets do not load Google Analytics on the host site.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">External API Calls</h2>
        <p className="text-muted-foreground">
          Some widgets make requests to third-party APIs for live data (e.g., exchange rates, gold prices, prayer times). These requests go directly from the user's browser to the third-party service. EmbedKit does not proxy, log, or intercept these requests. Please refer to the respective API provider's privacy policy for how they handle requests.
        </p>
        <ul className="list-disc pl-5 text-muted-foreground space-y-1">
          <li><strong>Exchange rates:</strong> Frankfurter API, Open Exchange Rates API</li>
          <li><strong>Gold/silver prices:</strong> Gold-API.com</li>
          <li><strong>Prayer times & Hijri dates:</strong> AlAdhan API</li>
          <li><strong>QR codes:</strong> goqr.me API</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">Embedded Widgets</h2>
        <p className="text-muted-foreground">
          When website owners embed our widgets via iframes, the widget runs in a sandboxed environment. The embedding website cannot access the widget's internal state, and the widget cannot access the embedding website's data. No cross-site data sharing occurs.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">Contact</h2>
        <p className="text-muted-foreground">
          If you have any questions about this privacy policy, please contact us at{" "}
          <a href="https://synergaid.com.au" target="_blank" rel="noopener noreferrer" className="text-primary underline">
            synergaid.com.au
          </a>.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold">Operator</h2>
        <p className="text-muted-foreground">
          EmbedKit is operated by Synergaid Pty Ltd (ABN: 63 682 263 001), an Australian company.
        </p>
      </section>
    </div>
  </div>
);

export default Privacy;
